import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CompanyUsersTable from './CompanyUsersTable';
import classNames from 'classnames';
import useRequest from 'hooks/useRequest';
import JobFunction from 'store/types/JobFunction';
import UserService from 'services/api/UserService';
import CompanyService from 'services/api/CompanyService';
import DirectoryListingService from 'services/api/DirectoryListingService';
import CompanyUser from 'store/types/CompanyUser';
import Alert from '@mui/material/Alert';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import Spinner from 'components/shared/Spinner';
import AddCompanyUserModal from './AddCompanyUserModal';
import DirectoryListingsTable from './DirectoryListingsTable';
import LinkCompanyUserModal from './LinkCompanyUserModal';
import { ConfigContext } from 'components/ConfigGuard';
import { ParametersContext } from 'components/ParametersGuard';
import { CompanyPaymentDetails } from 'store/types/Company';
import { CompanyEmployeeAccessRequestsData } from 'store/types/CompanyEmployeeAccessRequest';
import { DirectoryListings } from 'store/types/DirectoryListing';
import SelectOption from 'store/types/SelectOption';
import { getAvailablePortalRoles } from 'util/User';
import { UserContext } from 'components/UserGuard';

import commonStyles from 'styles/common.module.scss';
import pageStyles from '../CompanyPageView/CompanyPageView.module.scss';
import styles from './CompanyUsersSection.module.scss';

interface CompanyUsersSectionProps {
  companyId: string;
  refetchCount: () => void;
}

const CompanyUsersSection: React.FunctionComponent<CompanyUsersSectionProps> = ({ companyId, refetchCount }) => {
  const {
    companyManagement: { managementTableHeader: adminsTableHeader },
  } = useContext(ParametersContext);
  const {
    isBayViewTheme,
    modulesSettings: {
      companyManagement: { enableDirectoryListings },
    },
  } = useContext(ConfigContext);
  const { functions } = useContext(UserContext);
  const companyUsersRequest = useMemo(
    () => (companyId ? () => CompanyService.getCompanyUsers(companyId) : undefined),
    [companyId]
  );
  const companyEmployeeAccessRequests = useMemo(
    () => (companyId ? () => CompanyService.getCompanyEmployeeAccessRequests(companyId) : undefined),
    [companyId]
  );
  const companyDetailsRequest = useMemo(
    () => (companyId ? () => CompanyService.getCompanyPaymentDetails(companyId) : undefined),
    [companyId]
  );
  const directoryRequest = useMemo(
    () =>
      enableDirectoryListings && companyId ? () => DirectoryListingService.getDirectoryListings(companyId) : undefined,
    [companyId, enableDirectoryListings]
  );

  const {
    data: jobFunctions,
    loading: jobFunctionsLoading,
    error: jobFunctionsError,
  } = useRequest<JobFunction[]>(UserService.getJobFunctionList);
  const companyUsersRequestData = useRequest<CompanyUser[]>(companyUsersRequest);
  const companyEmployeeAccessRequestsData =
    useRequest<CompanyEmployeeAccessRequestsData>(companyEmployeeAccessRequests);
  const {
    data: companyDetails,
    loading: companyDetailsLoading,
    error: companyDetailsError,
  } = useRequest<CompanyPaymentDetails>(companyDetailsRequest);
  const { data: companyUsers, refetch: refetchCompanyUsers } = companyUsersRequestData;
  const { data: employeeAccessRequests, refetch: refetchEmployeeAccessRequests } = companyEmployeeAccessRequestsData;
  const { data: directoryListings, refetch: refetchDirectoryListings } =
    useRequest<DirectoryListings>(directoryRequest);

  const [addUserModalOpen, setAddUserModalOpen] = useState<boolean>(false);
  const [linkUserModalOpen, setLinkUserModalOpen] = useState<boolean>(false);
  const [selectedDirectoryNetworkId, setSelectedDirectoryNetworkId] = useState<string>();
  const [portalRoles, setPortalRoles] = useState<SelectOption[]>([]);

  const availableDirectoryNetworks = useMemo(() => {
    const listingsByNetworkId = new Map(
      (directoryListings?.directoryNetworks ?? []).map((network) => [network.directoryNetworkId, network])
    );

    return (
      companyDetails?.memberClassification?.map(
        ({ id, name }) =>
          listingsByNetworkId.get(id) ?? {
            directoryNetworkId: id,
            directoryNetworkName: name,
            listings: [],
          }
      ) ?? []
    );
  }, [companyDetails?.memberClassification, directoryListings?.directoryNetworks]);

  const selectedDirectoryNetwork = useMemo(
    () => availableDirectoryNetworks.find((network) => network.directoryNetworkId === selectedDirectoryNetworkId),
    [availableDirectoryNetworks, selectedDirectoryNetworkId]
  );

  const managementSectionTitle = useMemo(
    () => (isBayViewTheme ? 'Household Management' : 'Company Management'),
    [isBayViewTheme]
  );

  const addMemberButtonLabel = useMemo(() => (isBayViewTheme ? 'Add Family Member' : 'Add Person'), [isBayViewTheme]);

  const managementTableHeader = useMemo(() => {
    if (!adminsTableHeader) {
      return adminsTableHeader;
    }

    return isBayViewTheme
      ? adminsTableHeader.replace(/Company Employees/gi, 'Household family members')
      : adminsTableHeader;
  }, [adminsTableHeader, isBayViewTheme]);

  useEffect(() => {
    if (jobFunctions?.length) {
      const userRoles: string[] = functions?.find((company) => company.companyId === companyId)?.jobFunctionNames || [];
      const portalRoles: SelectOption[] = getAvailablePortalRoles(jobFunctions, userRoles);

      setPortalRoles(portalRoles);
    }
  }, [companyId, functions, jobFunctions]);

  const refetchRequests = useCallback(async () => {
    await Promise.all([refetchCompanyUsers(), refetchEmployeeAccessRequests(), refetchDirectoryListings()]);

    refetchCount();
  }, [refetchCompanyUsers, refetchCount, refetchDirectoryListings, refetchEmployeeAccessRequests]);

  const handleAddUserModalOpen = useCallback(() => {
    setAddUserModalOpen(true);
    setLinkUserModalOpen(false);
  }, []);

  const handleAddUserModalClose = useCallback(() => {
    setAddUserModalOpen(false);
  }, []);

  const handleLinkUserModalOpen = useCallback(() => {
    setLinkUserModalOpen(true);
    setAddUserModalOpen(false);
  }, []);

  const handleLinkUserModalClose = useCallback(() => {
    setLinkUserModalOpen(false);
  }, []);

  const handleAddAdministrator = useCallback(() => {
    handleLinkUserModalOpen();
  }, [handleLinkUserModalOpen]);

  return (
    <Spinner loading={jobFunctionsLoading || companyDetailsLoading} transparent={false}>
      {jobFunctionsError || companyDetailsError ? (
        <Alert severity={'error'} className={commonStyles.alert}>
          {jobFunctionsError || companyDetailsError}
        </Alert>
      ) : (
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps} xs={true} className={pageStyles.wrapper}>
            <h2 className={classNames(commonStyles.subTitle, commonStyles.uppercase, pageStyles.title)}>
              {managementSectionTitle}
            </h2>
          </Grid>
          <Grid {...defaultGridItemProps} md={'auto'}>
            <div className={styles.addPersonButton}>
              <Button
                color={'secondary'}
                variant={'contained'}
                className={styles.iconButton}
                onClick={handleAddAdministrator}
              >
                <Add /> {addMemberButtonLabel}
              </Button>
            </div>
          </Grid>
          {availableDirectoryNetworks.length > 0 && (
            <Grid {...defaultGridItemProps}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  px: 3,
                  py: 1.5,
                  backgroundColor: '#f7f8fb',
                }}
              >
                <Typography
                  variant={'caption'}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                    marginRight: 1,
                  }}
                >
                  {'Filter By Network For Directory Listing'}
                </Typography>

                {availableDirectoryNetworks.map((network) => {
                  const selected = selectedDirectoryNetworkId === network.directoryNetworkId;

                  return (
                    <Button
                      key={network.directoryNetworkId}
                      color={'secondary'}
                      variant={selected ? 'contained' : 'outlined'}
                      size={'small'}
                      onClick={() =>
                        setSelectedDirectoryNetworkId((currentId) =>
                          currentId === network.directoryNetworkId ? undefined : network.directoryNetworkId
                        )
                      }
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        borderRadius: '999px',
                        textTransform: 'none',
                        backgroundColor: selected ? undefined : 'background.paper',
                      }}
                    >
                      {network.directoryNetworkName}
                    </Button>
                  );
                })}
              </Box>
            </Grid>
          )}
          <Grid {...defaultGridItemProps}>
            {selectedDirectoryNetwork ? (
              <DirectoryListingsTable
                key={selectedDirectoryNetwork.directoryNetworkId}
                companyId={companyId}
                network={selectedDirectoryNetwork}
                companyUsers={companyUsers}
                refetch={refetchDirectoryListings}
              />
            ) : (
              <>
                {managementTableHeader && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: managementTableHeader,
                    }}
                  />
                )}

                <CompanyUsersTable
                  {...companyUsersRequestData}
                  refetch={refetchRequests}
                  companyId={companyId}
                  jobFunctionsList={jobFunctions || []}
                  companyRequests={employeeAccessRequests}
                  directoryListings={directoryListings}
                  showDirectoryStatus={availableDirectoryNetworks.length > 0}
                  refetchDirectoryListings={refetchDirectoryListings}
                />
              </>
            )}
          </Grid>
        </Grid>
      )}
      {linkUserModalOpen && (
        <LinkCompanyUserModal
          companyMembers={companyUsers}
          companyRequests={employeeAccessRequests}
          open={linkUserModalOpen}
          onClose={handleLinkUserModalClose}
          onAddUserModalOpen={handleAddUserModalOpen}
          companyId={companyId}
          portalRolesList={portalRoles || []}
          refetch={refetchRequests}
        />
      )}
      {addUserModalOpen && (
        <AddCompanyUserModal
          predefinedAddress={companyDetails?.address}
          memberClassification={companyDetails?.memberClassification}
          open={addUserModalOpen}
          onClose={handleAddUserModalClose}
          companyId={companyId}
          portalRolesList={portalRoles || []}
          refetch={refetchRequests}
        />
      )}
    </Spinner>
  );
};

export default CompanyUsersSection;
